import type { ProductPlacementEntry } from '../types/index.js';

export interface NamedProduct {
  id: string;
  name: string;
}

const PLACEMENT_FLAGS: Array<{
  key: keyof NonNullable<ProductPlacementEntry['placements']>;
  label: string;
}> = [
  { key: 'general_shelf', label: 'General Shelf' },
  { key: 'fridge_placement', label: 'Fridge' },
  { key: 'dry_shelf_placement', label: 'Dry Shelf' },
  { key: 'poster_placement', label: 'Poster' },
  { key: 'display_stand', label: 'Display Stand' },
  { key: 'checkout_placement', label: 'Checkout' },
];

export function parseJsonValue<T>(value: unknown, fallback: T): T {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

export function collectVisitProductIds(
  visitProducts: unknown,
  placements: unknown,
  fallbackProductId?: string | null
): string[] {
  const fromArray = parseJsonValue<string[]>(visitProducts, []);
  const fromPlacements = Object.keys(
    parseJsonValue<Record<string, unknown>>(placements, {})
  );
  const ids = [
    ...(Array.isArray(fromArray) ? fromArray : []),
    ...fromPlacements,
  ];
  if (fallbackProductId) ids.push(fallbackProductId);
  return [...new Set(ids.filter(Boolean))];
}

export function anyPlacementSecured(placements: unknown): boolean {
  const map = parseJsonValue<Record<string, ProductPlacementEntry>>(
    placements,
    {}
  );
  return Object.values(map).some((entry) => entry?.secured === true);
}

export function resolveProductName(
  productId: string,
  products: NamedProduct[]
): string | null {
  const match = products.find((p) => p.id === productId);
  return match?.name || null;
}

export function visibleProductNames(
  productIds: string[],
  products: NamedProduct[],
  max = 6
): { names: string[]; extra: number } {
  const names = productIds
    .map((id) => resolveProductName(id, products))
    .filter((name): name is string => Boolean(name));
  if (names.length <= max) return { names, extra: 0 };
  return { names: names.slice(0, max), extra: names.length - max };
}

export function placementSnapshotLabels(
  placements: unknown
): string[] {
  const map = parseJsonValue<Record<string, ProductPlacementEntry>>(
    placements,
    {}
  );
  const labels = new Set<string>();
  for (const entry of Object.values(map)) {
    const block = entry?.placements;
    if (!block) continue;
    for (const flag of PLACEMENT_FLAGS) {
      if (block[flag.key]) labels.add(flag.label);
    }
  }
  return [...labels];
}
